import React from 'react';
import { Layout, Menu, theme, Button, Space } from 'antd';
import { DashboardOutlined, LogoutOutlined, CloudOutlined, AppstoreOutlined, VideoCameraOutlined, PlayCircleOutlined, SoundOutlined, FileImageOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

const { Header, Content, Sider } = Layout;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { t } = useTranslation();
  
  const {
    token: { colorBgContainer, colorBgElevated, borderRadiusLG },
  } = theme.useToken();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Determine which menu item is currently selected based on the path
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.startsWith('/categories')) return 'categories';
    if (path.startsWith('/token-deliveries')) return 'token-deliveries';
    if (path.startsWith('/deliveries')) return 'deliveries';
    if (path.startsWith('/incoming')) return 'incoming';
    if (path.startsWith('/streams')) return 'streams';
    if (path.startsWith('/advertising')) return 'advertising';
    return 'dashboard';
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ padding: 0, background: colorBgElevated, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="logo" style={{ margin: '0 24px' }}>
          <h1>{t('app.title')}</h1>
        </div>
        <Space style={{ marginRight: 24 }}>
          <LanguageSwitcher />
          <span style={{ marginRight: 16 }}>{t('dashboard.welcome', { username: user?.username })}</span>
          <Button icon={<LogoutOutlined />} onClick={handleLogout}>
            {t('auth.logout')}
          </Button>
        </Space>
      </Header>
      <Layout>
        <Sider width={250} style={{ background: colorBgContainer }}>
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            <Menu
              mode="inline"
              selectedKeys={[getSelectedKey()]}
              style={{ 
                flex: 1, 
                borderRight: 0 
              }}
              items={[
                {
                  key: 'dashboard',
                  icon: <DashboardOutlined />,
                  label: t('dashboard.title'),
                  onClick: () => handleNavigation('/'),
                },
                {
                  key: 'streams',
                  icon: <VideoCameraOutlined />,
                  label: t('streams.title'),
                  onClick: () => handleNavigation('/streams'),
                },
                {
                  key: 'incoming',
                  icon: <PlayCircleOutlined />,
                  label: t('incoming.title'),
                  onClick: () => handleNavigation('/incoming'),
                },
                {
                  key: 'categories',
                  icon: <AppstoreOutlined />,
                  label: t('categories.title'),
                  onClick: () => handleNavigation('/categories'),
                },
                {
                  key: 'deliveries',
                  icon: <CloudOutlined />,
                  label: t('deliveries.title'),
                  onClick: () => handleNavigation('/deliveries'),
                },
                {
                  key: 'token-deliveries',
                  icon: <LinkOutlined />,
                  label: t('tokenDeliveries.title'),
                  onClick: () => handleNavigation('/token-deliveries'),
                },
                {
                  key: 'advertising',
                  icon: <SoundOutlined />,
                  label: t('advertising.title'),
                  children: [
                    {
                      key: 'advertising-sets',
                      label: t('advertising.sets.title'),
                      onClick: () => handleNavigation('/advertising/sets'),
                    },
                    {
                      key: 'advertising-advs',
                      label: t('advertising.advs.title'),
                      onClick: () => handleNavigation('/advertising/advs'),
                    },
                    {
                      key: 'advertising-clients',
                      label: t('advertising.clients.title'),
                      onClick: () => handleNavigation('/advertising/clients'),
                    },
                    {
                      key: 'advertising-logos',
                      label: t('logos.title'),
                      onClick: () => handleNavigation('/advertising/logos'),
                    },
                  ],
                }
              ]}
            />
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid #f0f0f0',
              fontSize: '12px',
              color: '#8c8c8c',
              textAlign: 'center',
              marginTop: 'auto'
            }}>
              deepStream v1.4.1
            </div>
          </div>
        </Sider>
        <Layout style={{ padding: '24px' }}>
          <Content
            style={{
              margin: 0,
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default HomePage; 